import React, { lazy, Suspense, useContext, useEffect, useRef } from "react";
import { Box, CircularProgress, CssBaseline, ThemeProvider } from "@mui/material";
import {
  Routes,
  Route,
  BrowserRouter,
  Navigate,
  useParams,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import {
  HOOPER_DARK_GRAY,
  HOOPER_NEON_GREEN,
} from "./shared/constants";
// Loaded before <BrowserRouter> mounts, so its popstate listener runs before
// the router's (see use-leave-guard.ts).
import "./lib/use-leave-guard";
import VerifyView from "./views/verify";
import { AuthProvider, AuthContext } from "./context/auth";
import { ViewModeProvider } from "./context/view-mode";
import { I18nProvider } from "./context/i18n";
import HubMaintenanceBlocker from "./components/HubMaintenanceBlocker";
import { analytics } from "./shared/utils";
import { ActiveAccountProvider } from "./context/active-account";
import { HubDataProvider } from "./context/hub-data";
import { TourProvider } from "./components/tour/TourProvider";
import theme, { THEME_MODE_STORAGE_KEY } from "./shared/theme";

// Code-split on purpose. The public share pages are what logged-out
// visitors land on from a text message or a QR code; they must not pull in
// the dashboard (charts, video editor, admin), and the dashboard must not
// pull them in either.
const DashboardRoutes = lazy(() => import("./DashboardRoutes"));
const SharedSessionEntry = lazy(() => import("./views/public/shared-session"));
const PublicReferralView = lazy(() => import("./views/public/referral"));
const PublicTeamView = lazy(() => import("./views/public/team"));
const PublicJoinView = lazy(() => import("./views/public/join"));
const DeleteAccountPage = lazy(() => import("./views/public/delete-account"));

const SharedSessionLegacyRedirect = () => {
  const { sessionId } = useParams();
  return <Navigate to={`/shared/${sessionId}`} replace />;
};

const RouteFallback = () => (
  <Box
    data-testid="route-loading"
    sx={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CircularProgress size={30} />
  </Box>
);

const PostHogAuthSync = ({ children }: { children: React.ReactNode }) => {
  const { user, isImpersonatingFromAdmin } = useContext(AuthContext);
  const identifiedIdentityKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const userId = user?.id || user?.userId;

    if (!userId) {
      if (identifiedIdentityKeyRef.current) {
        analytics.reset();
        identifiedIdentityKeyRef.current = null;
      }
      return;
    }

    const identityKey = `${isImpersonatingFromAdmin ? "impersonated" : "direct"}:${userId}`;
    if (identifiedIdentityKeyRef.current === identityKey) return;

    if (identifiedIdentityKeyRef.current) {
      analytics.reset();
    }

    analytics.identify(userId, {
      email: user.email,
      username: user.username,
      tag: user.tag,
      role: user.role,
      userType: user.userType,
      userSegmentType: user.userSegmentType,
      language: user.language,
      isChild: user.isChild,
      subscriptionType: user.subscriptionType,
      isImpersonatingFromAdmin,
    });
    identifiedIdentityKeyRef.current = identityKey;
  }, [isImpersonatingFromAdmin, user]);

  return <>{children}</>;
};

function App() {
  return (
    <div className="App">
      <ThemeProvider
        theme={theme}
        defaultMode="system"
        modeStorageKey={THEME_MODE_STORAGE_KEY}
      >
        <CssBaseline />
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 2000,
            style: {
              background: HOOPER_DARK_GRAY,
              color: HOOPER_NEON_GREEN,
            },
          }}
        />
        <BrowserRouter>
          <I18nProvider>
            <Routes>
              <Route path="/verify" element={<VerifyView />} />
            </Routes>
            <Routes>
              <Route path="/delete-account" element={<Suspense fallback={<RouteFallback />}><DeleteAccountPage /></Suspense>} />
              <Route path="*" element={<AuthProvider>
              <ActiveAccountProvider>
              <HubDataProvider>
              <PostHogAuthSync>
                <ViewModeProvider>
                  <TourProvider>
                  <Suspense fallback={<RouteFallback />}>
                    <Routes>
                      {/*
                        Public pages: no login, and outside
                        HubMaintenanceBlocker so a share link or team QR
                        still works while the Hub is in maintenance.
                      */}
                      <Route path="/shared/:sessionId" element={<SharedSessionEntry />} />
                      <Route
                        path="/shared/:sessionId/player/:playerId"
                        element={<SharedSessionEntry />}
                      />
                      <Route
                        path="/shared/:sessionId/:page"
                        element={<SharedSessionLegacyRedirect />}
                      />
                      <Route path="/i/:code" element={<PublicReferralView />} />
                      <Route path="/t/:code" element={<PublicTeamView />} />
                      <Route path="/join/:guestId" element={<PublicJoinView />} />
                      <Route
                        path="*"
                        element={
                          <HubMaintenanceBlocker>
                            <DashboardRoutes />
                          </HubMaintenanceBlocker>
                        }
                      />
                    </Routes>
                  </Suspense>
                  </TourProvider>
                </ViewModeProvider>
              </PostHogAuthSync>
              </HubDataProvider>
              </ActiveAccountProvider>
            </AuthProvider>} />
            </Routes>
          </I18nProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
